import React, { useRef, useState } from 'react';
import {
  Paper,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Button,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Divider,
  Slider,
  Input,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';
import { GridView, ViewList, ViewCarousel, FormatBold, FormatItalic, RestartAlt } from '@mui/icons-material';
import { saveCategorySettings } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

type MenuDropdownStyleOption = 'list' | 'minimal' | 'classic';

/** Küçük şablon önizlemesi — stil kartlarında kullanılır */
const MenuDropdownStyleThumb: React.FC<{
  style: MenuDropdownStyleOption;
  sampleImages?: string[];
}> = ({ style, sampleImages = [] }) => {
  const imgs = [0, 1, 2, 3].map((i) => sampleImages[i] || '');

  if (style === 'list') {
    return (
      <Box
        sx={{
          height: 88,
          bgcolor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.6,
          justifyContent: 'center',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              height: 10,
              width: `${55 + (i % 3) * 12}%`,
              borderRadius: 0.5,
              bgcolor: i === 0 ? '#111827' : '#d1d5db',
            }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 88,
        bgcolor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 1,
        p: style === 'classic' ? 0 : 0.75,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: style === 'classic' ? 0 : 0.5,
        overflow: 'hidden',
      }}
    >
      {imgs.map((src, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.4,
            p: style === 'classic' ? 0.75 : 0.5,
            borderRight: style === 'classic' && i < 3 ? '1px solid #f3f4f6' : 'none',
            bgcolor: style === 'classic' ? 'transparent' : '#fafafa',
            borderRadius: style === 'classic' ? 0 : 0.5,
          }}
        >
          <Box
            sx={{
              width: '100%',
              aspectRatio: '1',
              borderRadius: 0.5,
              bgcolor: '#e5e7eb',
              backgroundImage: src ? `url(${src})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <Box sx={{ width: '70%', height: 4, borderRadius: 0.5, bgcolor: '#d1d5db' }} />
        </Box>
      ))}
    </Box>
  );
};

// SQL'den gelen MenuCategory tipi
interface MenuCategory {
  // SQL alan adları
  categoryId: string;
  categoryName: string;
  isVisible: boolean;
  displayOrder: number;
  categoryImageUrl?: string;
  bannerImageUrl?: string;
  // Eski alan adları (geriye uyumluluk)
  id?: string;
  name?: string;
  visible?: boolean;
  order?: number;
  imageUrl?: string;
  subCategories?: MenuCategory[];
}

type DisplayStyle = 'grid' | 'list' | 'carousel' | 'modern-grid' | 'modern-list' | 'modern-carousel';

interface CategorySettings {
  showOnHomepage: boolean;
  displayStyle: {
    web: DisplayStyle;
    mobile: DisplayStyle;
  };
  itemsPerRow: number;
  titleStyles: {
    fontSize: number;
    color: string;
    bold: boolean;
    italic: boolean;
    fontFamily: string;
    strokeWidth: number;
    strokeColor: string;
  };
  title?: string;
  menuDropdownStyle?: 'list' | 'minimal' | 'classic';
  menuDropdownColumns?: number;
  menuDropdownMaxHeight?: number;
}

interface CategorySettingsComponentProps {
  settings: CategorySettings;
  onSettingsChange: (setting: keyof CategorySettings, value: any) => void;
  menuCategories: MenuCategory[];
  companyId: string | null;
  configId: string | null;
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
  }
}));

const MenuDropdownPreviewRenderer: React.FC<{
  settings: CategorySettings;
  menuCategories: MenuCategory[];
}> = ({ settings, menuCategories }) => {
  const visibleCategories = menuCategories
    .filter((cat) => cat.isVisible ?? cat.visible)
    .slice(0, 6);
  const isList = settings.menuDropdownStyle === 'list';
  const isClassic = settings.menuDropdownStyle === 'classic';
  const columns = settings.menuDropdownColumns || 4;
  const maxHeight = settings.menuDropdownMaxHeight || 400;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          bgcolor: '#fff',
          fontSize: 13,
          fontWeight: 600,
          color: '#374151',
        }}
      >
        Tüm Kategoriler ▾
      </Box>

      <Box
        sx={{
          mt: 0.5,
          border: '1px solid #e5e7eb',
          borderRadius: 1.5,
          bgcolor: '#fff',
          boxShadow: isClassic ? '0 16px 40px rgba(0,0,0,0.12)' : '0 8px 24px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          maxWidth: isList ? 280 : '100%',
        }}
      >
        {isList ? (
          <Box sx={{ p: 1, maxHeight: 220, overflowY: 'auto' }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1.5,
                pb: 1,
                mb: 0.5,
                borderBottom: '1px solid #f3f4f6',
                color: '#6b7280',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Tüm Kategoriler
            </Typography>
            {visibleCategories.map((category) => (
              <Typography
                key={category.categoryId || category.id}
                variant="body2"
                sx={{
                  px: 1.5,
                  py: 1,
                  fontWeight: 600,
                  color: '#111827',
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#f3f4f6' },
                }}
              >
                {category.categoryName || category.name}
              </Typography>
            ))}
          </Box>
        ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            gap: isClassic ? 0 : 1,
            p: isClassic ? 0 : 1.5,
            maxHeight: Math.min(maxHeight, 220),
            overflowY: 'auto',
          }}
        >
          {visibleCategories.map((category) => (
            <Box
              key={category.categoryId || category.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                p: isClassic ? 1.5 : 1,
                borderRight: isClassic ? '1px solid #f3f4f6' : 'none',
                borderBottom: isClassic ? '1px solid #f3f4f6' : 'none',
                borderRadius: isClassic ? 0 : 1,
                bgcolor: isClassic ? 'transparent' : '#fafafa',
              }}
            >
              <Box
                sx={{
                  width: isClassic ? 56 : 44,
                  height: isClassic ? 56 : 44,
                  borderRadius: 1,
                  bgcolor: '#e5e7eb',
                  backgroundImage: category.categoryImageUrl || category.imageUrl
                    ? `url(${category.categoryImageUrl || category.imageUrl})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  textAlign: 'center',
                  fontWeight: isClassic ? 600 : 500,
                  color: '#374151',
                  lineHeight: 1.2,
                }}
              >
                {category.categoryName || category.name}
              </Typography>
            </Box>
          ))}
        </Box>
        )}

        {!isList && isClassic && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f3f4f6', bgcolor: '#fafafa' }}>
            <Typography variant="caption" color="text.secondary">
              {visibleCategories.length} kategori mevcut
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CategoryPreviewRenderer: React.FC<{ settings: CategorySettings; menuCategories: MenuCategory[] }> = ({ settings, menuCategories }) => {
  // SQL formatını destekle (isVisible) ve geriye uyumluluk için (visible)
  const visibleCategories = menuCategories.filter(cat => cat.isVisible ?? cat.visible);

  const getTitleStyles = (isModern: boolean = false) => {
    const styles: React.CSSProperties = {};
    if (settings.titleStyles) {
      styles.fontFamily = settings.titleStyles.fontFamily || 'Inter';
      styles.fontSize = `${settings.titleStyles.fontSize}px`;
      // Modern stillerde renk genellikle beyazdır, bu yüzden ayardan gelen rengi ezeriz.
      // Diğerlerinde ise ayardan gelen rengi kullanırız.
      styles.color = isModern ? settings.titleStyles.color : 'inherit';
      styles.fontWeight = settings.titleStyles.bold ? 'bold' : 'normal';
      styles.fontStyle = settings.titleStyles.italic ? 'italic' : 'normal';
      
      // Dışa doğru kontur ayarları (text-shadow ile)
      if (settings.titleStyles.strokeWidth && settings.titleStyles.strokeWidth > 0) {
        const strokeColor = settings.titleStyles.strokeColor || '#000000';
        const width = settings.titleStyles.strokeWidth;
        
        // Çoklu text-shadow ile dışa doğru kontur oluştur
        const shadows = [];
        for (let x = -width; x <= width; x++) {
          for (let y = -width; y <= width; y++) {
            if (x !== 0 || y !== 0) {
              shadows.push(`${x}px ${y}px 0 ${strokeColor}`);
            }
          }
        }
        styles.textShadow = shadows.join(', ');
      }
    }
    return styles;
  };

  const itemsPerRow = settings.itemsPerRow || 4;
  const gridColumnValue = 12 / itemsPerRow;

  const renderGrid = (modern = false) => (
    <Grid container spacing={2}>
      {visibleCategories.map(category => {
        // SQL ve eski formatı destekle
        const catId = category.categoryId || category.id;
        const catName = category.categoryName || category.name || 'Kategori';
        const catImage = category.categoryImageUrl || category.imageUrl;
        
        return (
          <Grid item key={catId} xs={12} sm={6} md={gridColumnValue}>
            <Card sx={{ height: '100%', position: 'relative' }}>
               <CardMedia
                  component="img"
                  height={modern ? "200" : "140"}
                  image={catImage || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Hw7Zyc2VsIFlvazwvdGV4dD48L3N2Zz4='}
                  alt={catName}
                  sx={{ objectFit: 'cover' }}
                />
              {modern && <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }} />}
              <CardContent sx={modern ? { position: 'absolute', bottom: 0, left: 0, color: 'white' } : {}}>
                <Typography style={getTitleStyles(modern)}>
                  {catName}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
  
  const renderList = (modern = false) => (
     <Grid container spacing={2}>
      {visibleCategories.map(category => {
        // SQL ve eski formatı destekle
        const catId = category.categoryId || category.id;
        const catName = category.categoryName || category.name || 'Kategori';
        const catImage = category.categoryImageUrl || category.imageUrl;
        
        return (
          <Grid item key={catId} xs={12} sm={6}>
             <Card sx={{ display: 'flex', alignItems: 'center', position: 'relative', height: modern ? '120px' : 'auto' }}>
              <CardMedia
                component="img"
                sx={modern ? 
                  { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 } :
                  { width: 80, height: 80, objectFit: 'contain', p: 1 }
                }
                image={catImage || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Hw7Zyc2VsIFlvazwvdGV4dD48L3N2Zz4='}
                alt={catName}
              />
              {modern && <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.7), transparent)', zIndex: 2 }} />}
              <CardContent sx={modern ? { zIndex: 3, color: 'white' } : { flex: '1 0 auto' }}>
                <Typography component="div" variant="h6" style={getTitleStyles(modern)}>
                  {catName}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderCarousel = (modern = false) => (
    <Box sx={{ display: 'flex', overflowX: 'auto', pb: 2, gap: 2 }}>
      {visibleCategories.map(category => {
        // SQL ve eski formatı destekle
        const catId = category.categoryId || category.id;
        const catName = category.categoryName || category.name || 'Kategori';
        const catImage = category.categoryImageUrl || category.imageUrl;
        
        return (
          <Box key={catId} sx={{ flex: `0 0 calc(${100 / itemsPerRow}% - 12px)`, minWidth: '150px' }}>
            <Card sx={{ height: '100%', position: 'relative' }}>
               <CardMedia
                  component="img"
                  height={modern ? "180" : "120"}
                  image={catImage || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Hw7Zyc2VsIFlvazwvdGV4dD48L3N2Zz4='}
                  alt={catName}
                  sx={{ objectFit: 'cover' }}
                />
              {modern && <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }} />}
              <CardContent sx={modern ? { position: 'absolute', bottom: 0, left: 0, color: 'white', p: 2 } : { p: 2 }}>
                <Typography style={getTitleStyles(modern)} textAlign="center">
                  {catName}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        );
      })}
    </Box>
  );

  switch (settings.displayStyle.web) {
    case 'grid':
      return renderGrid();
    case 'modern-grid':
      return renderGrid(true);
    case 'list':
      return renderList();
    case 'modern-list':
      return renderList(true);
    case 'carousel':
      return renderCarousel();
    case 'modern-carousel':
      return renderCarousel(true);
    default:
      return renderGrid();
  }
};

const StyleButtonGroup: React.FC<{
  value: DisplayStyle;
  onChange: (value: DisplayStyle) => void;
}> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    <Button 
      variant={value === 'grid' ? 'contained' : 'outlined'}
      onClick={() => onChange('grid')}
      startIcon={<GridView />}
      size="small"
    >
      Grid
    </Button>
    <Button 
      variant={value === 'modern-grid' ? 'contained' : 'outlined'}
      onClick={() => onChange('modern-grid')}
      startIcon={<GridView />}
      size="small"
    >
      Modern Grid
    </Button>
    <Button 
      variant={value === 'list' ? 'contained' : 'outlined'}
      onClick={() => onChange('list')}
      startIcon={<ViewList />}
      size="small"
    >
      Liste
    </Button>
    <Button 
      variant={value === 'modern-list' ? 'contained' : 'outlined'}
      onClick={() => onChange('modern-list')}
      startIcon={<ViewList />}
      size="small"
    >
      Modern Liste
    </Button>
    <Button 
      variant={value === 'carousel' ? 'contained' : 'outlined'}
      onClick={() => onChange('carousel')}
      startIcon={<ViewCarousel />}
      size="small"
    >
      Carousel
    </Button>
    <Button 
      variant={value === 'modern-carousel' ? 'contained' : 'outlined'}
      onClick={() => onChange('modern-carousel')}
      startIcon={<ViewCarousel />}
      size="small"
    >
      Modern Carousel
    </Button>
  </Box>
);

const CategorySettingsComponent: React.FC<CategorySettingsComponentProps> = ({ settings, onSettingsChange, menuCategories, companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const [saveError, setSaveError] = useState<string | null>(null);
  const isConfigReady = Boolean(companyId && configId);
  
  // 🔍 DEBUG: Gelen settings değerlerini kontrol et
  console.log('🎨 [CATEGORY SETTINGS] Component render edildi, gelen settings:', {
    displayStyleWeb: settings.displayStyle?.web,
    displayStyleMobile: settings.displayStyle?.mobile,
    showOnHomepage: settings.showOnHomepage,
    itemsPerRow: settings.itemsPerRow,
    configId,
    isConfigReady,
  });
  
  // Auto-save için debounce timer
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-save fonksiyonu - güncel settings değerlerini parametre olarak alır (closure sorunu için)
  const autoSaveCategorySettings = async (currentSettings: CategorySettings) => {
    if (!companyId || !configId) {
      const message = 'ConfigId henüz hazır değil, kayıt atlandı.';
      console.warn('⚠️ [CATEGORY AUTO-SAVE]', message, { companyId, configId });
      setSaveError(message);
      return;
    }

    try {
      setSaveError(null);
      console.log('💾 [CATEGORY AUTO-SAVE] Kategori ayarları otomatik kaydediliyor...');
      console.log('📤 [CATEGORY AUTO-SAVE] Gönderilecek değerler:', {
        companyId,
        configId,
        webLayout: currentSettings.displayStyle.web,
        mobileLayout: currentSettings.displayStyle.mobile,
        showOnHomepage: currentSettings.showOnHomepage
      });

      // TitleStyles'ı JSON'a çevir
      const titleStylesJson = JSON.stringify(currentSettings.titleStyles);

      const categoryData = {
        companyId,
        configId,
        showCategoriesOnHomepage: currentSettings.showOnHomepage === true,
        homepageCategoryTitle: currentSettings.title || 'Kategoriler',
        homepageCategoryLayout: currentSettings.displayStyle.web,
        mobileCategoryLayout: currentSettings.displayStyle.mobile,
        categoriesPerRow: currentSettings.itemsPerRow,
        mobileCategoriesPerRow: 2, // Sabit değer
        titleStylesJson,
        menuDropdownStyle: currentSettings.menuDropdownStyle || 'minimal',
        menuDropdownColumns: currentSettings.menuDropdownColumns || 4,
        menuDropdownMaxHeight: currentSettings.menuDropdownMaxHeight || 400,
      };

      const result = await saveCategorySettings(categoryData);
      console.log('✅ [CATEGORY AUTO-SAVE] Kategori ayarları otomatik kaydedildi');
      console.log('📥 [CATEGORY AUTO-SAVE] Backend yanıtı:', result);
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (error) {
      console.error('❌ [CATEGORY AUTO-SAVE] Otomatik kayıt hatası:', error);
      setSaveError('Kategori ayarları kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  const handleDisplayStyleChange = (platform: 'web' | 'mobile', value: DisplayStyle) => {
    console.log('🔄 [CATEGORY SETTINGS] Display style değişiyor:', {
      platform,
      eskiDeger: settings.displayStyle?.[platform],
      yeniDeger: value
    });
    
    // Yeni settings değerini oluştur
    const newDisplayStyle = {
      ...settings.displayStyle,
      [platform]: value,
    };
    
    const newSettings: CategorySettings = {
      ...settings,
      displayStyle: newDisplayStyle
    };
    
    // Parent state'i güncelle
    onSettingsChange('displayStyle', newDisplayStyle);

    // Auto-save tetikle - güncel settings'i parametre olarak geç (closure sorunu için)
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      autoSaveCategorySettings(newSettings);
    }, 1000);
  };

  const handleMenuDropdownChange = <K extends 'menuDropdownStyle' | 'menuDropdownColumns' | 'menuDropdownMaxHeight'>(
    key: K,
    value: CategorySettings[K]
  ) => {
    const newSettings: CategorySettings = {
      ...settings,
      [key]: value,
    };

    onSettingsChange(key, value);

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      autoSaveCategorySettings(newSettings);
    }, 1000);
  };

  const handleTitleStyleChange = (property: string, value: any) => {
    // Yeni titleStyles değerini oluştur
    const newTitleStyles = {
      ...settings.titleStyles,
      [property]: value,
    };
    
    const newSettings: CategorySettings = {
      ...settings,
      titleStyles: newTitleStyles
    };
    
    onSettingsChange('titleStyles', newTitleStyles);

    // Auto-save tetikle - güncel settings'i parametre olarak geç
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      autoSaveCategorySettings(newSettings);
    }, 1000);
  };

  const resetTitleStyles = () => {
    onSettingsChange('titleStyles', {
      fontSize: 16,
      color: '#ffffff',
      bold: true,
      italic: false,
      fontFamily: 'Inter',
      strokeWidth: 0,
      strokeColor: '#000000',
    });
  };

  const fontOptions = [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Raleway', 'Poppins', 'Nunito', 'Merriweather'
  ];

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      
      <SectionTitle variant="h6">
        <CategoryIcon /> {t('ecommerceCategorySettings.title')}
      </SectionTitle>

      {/* Üstte: Tüm Kategoriler dropdown şablonları (thumbnail) */}
      <Box
        id="menu-category-dropdown-settings"
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.light',
          bgcolor: 'rgba(25, 118, 210, 0.04)',
        }}
      >
        <SectionTitle variant="h6" sx={{ mb: 1 }}>
          {t('ecommerceCategorySettings.menuDropdownTitle')}
        </SectionTitle>

        <Alert severity="info" sx={{ mb: 2 }}>
          {t('ecommerceCategorySettings.menuDropdownWhereHint')}
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ecommerceCategorySettings.menuDropdownDescription')}
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5 }}>
          {t('ecommerceCategorySettings.menuDropdownStyleTemplates')}
        </Typography>

        {(() => {
          const selectedStyle = (settings.menuDropdownStyle || 'minimal') as MenuDropdownStyleOption;
          const sampleImages = menuCategories
            .filter((cat) => cat.isVisible ?? cat.visible)
            .map((cat) => cat.categoryImageUrl || cat.imageUrl || '')
            .filter(Boolean)
            .slice(0, 4) as string[];
          const templates: Array<{
            value: MenuDropdownStyleOption;
            title: string;
            hint: string;
          }> = [
            {
              value: 'list',
              title: t('ecommerceCategorySettings.menuDropdownStyleList'),
              hint: t('ecommerceCategorySettings.menuDropdownStyleListHint'),
            },
            {
              value: 'minimal',
              title: t('ecommerceCategorySettings.menuDropdownStyleMinimal'),
              hint: t('ecommerceCategorySettings.menuDropdownStyleMinimalHint'),
            },
            {
              value: 'classic',
              title: t('ecommerceCategorySettings.menuDropdownStyleClassic'),
              hint: t('ecommerceCategorySettings.menuDropdownStyleClassicHint'),
            },
          ];

          return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {templates.map((tpl) => {
                const selected = selectedStyle === tpl.value;
                return (
                  <Grid item xs={12} sm={4} key={tpl.value}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => handleMenuDropdownChange('menuDropdownStyle', tpl.value)}
                      sx={{
                        width: '100%',
                        textAlign: 'left',
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: selected ? 'rgba(25, 118, 210, 0.06)' : '#fff',
                        p: 1.5,
                        font: 'inherit',
                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        boxShadow: selected ? '0 0 0 3px rgba(25,118,210,0.15)' : 'none',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', mb: 1 }}>
                        <MenuDropdownStyleThumb style={tpl.value} sampleImages={sampleImages} />
                        {selected && (
                          <CheckCircleIcon
                            color="primary"
                            sx={{ position: 'absolute', top: 6, right: 6, bgcolor: '#fff', borderRadius: '50%' }}
                            fontSize="small"
                          />
                        )}
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {tpl.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {tpl.hint}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}

        <Grid container spacing={3}>
          {settings.menuDropdownStyle !== 'list' && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ecommerceCategorySettings.menuDropdownColumns')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={settings.menuDropdownColumns || 4}
                  onChange={(_, value) => {
                    if (value !== null) {
                      handleMenuDropdownChange('menuDropdownColumns', value);
                    }
                  }}
                >
                  {[3, 4, 5].map((count) => (
                    <ToggleButton key={count} value={count}>
                      {count}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ecommerceCategorySettings.menuDropdownMaxHeight')} ({settings.menuDropdownMaxHeight || 400}px)
                </Typography>
                <Slider
                  value={settings.menuDropdownMaxHeight || 400}
                  min={320}
                  max={480}
                  step={20}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => handleMenuDropdownChange('menuDropdownMaxHeight', value as number)}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {t('ecommerceCategorySettings.menuDropdownPreviewTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ecommerceCategorySettings.menuDropdownPreviewDescription')}
            </Typography>
            {menuCategories.filter((cat) => cat.isVisible ?? cat.visible).length > 0 ? (
              <Box sx={{ p: 2, border: '1px dashed grey', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                <MenuDropdownPreviewRenderer settings={settings} menuCategories={menuCategories} />
              </Box>
            ) : (
              <Alert severity="info">{t('ecommerceCategorySettings.previewEmpty')}</Alert>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.showOnHomepage}
                disabled={!isConfigReady}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  const newSettings: CategorySettings = {
                    ...settings,
                    showOnHomepage: newValue
                  };
                  
                  onSettingsChange('showOnHomepage', newValue);
                  
                  // Auto-save tetikle - güncel settings'i parametre olarak geç
                  if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                  }
                  saveTimerRef.current = setTimeout(() => {
                    autoSaveCategorySettings(newSettings);
                  }, 1000);
                }}
              />
            }
            label={t('ecommerceCategorySettings.showOnHomepage')}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('ecommerceCategorySettings.webViewStyle')}
          </Typography>
          <StyleButtonGroup 
            value={settings.displayStyle?.web || 'grid'}
            onChange={(value) => handleDisplayStyleChange('web', value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('ecommerceCategorySettings.mobileViewStyle')}
          </Typography>
          <StyleButtonGroup 
            value={settings.displayStyle?.mobile || 'grid'}
            onChange={(value) => handleDisplayStyleChange('mobile', value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <SectionTitle variant="h6">
        {t('ecommerceCategorySettings.titleStyles')}
      </SectionTitle>

      <Grid container spacing={2} alignItems="center">
        {/* Font Family Dropdown */}
        <Grid item xs={12} sm={6} md={3}>
           <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t('ecommerceCategorySettings.fields.fontFamily')}</InputLabel>
            <Select
              value={settings.titleStyles?.fontFamily || 'Inter'}
              onChange={(e) => handleTitleStyleChange('fontFamily', e.target.value as string)}
              label={t('ecommerceCategorySettings.fields.fontFamily')}
            >
              {fontOptions.map(font => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>{font}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Font Size Dropdown */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t('ecommerceCategorySettings.fields.fontSize')}</InputLabel>
            <Select
              value={settings.titleStyles?.fontSize || 16}
              onChange={(e) => handleTitleStyleChange('fontSize', e.target.value as number)}
              label={t('ecommerceCategorySettings.fields.fontSize')}
            >
              {[10, 12, 14, 16, 18, 20, 24, 28, 32].map(size => (
                <MenuItem key={size} value={size}>{size} px</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Stroke Width Dropdown */}
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t('ecommerceCategorySettings.fields.strokeWidth')}</InputLabel>
            <Select
              value={settings.titleStyles?.strokeWidth || 0}
              onChange={(e) => handleTitleStyleChange('strokeWidth', e.target.value as number)}
              label={t('ecommerceCategorySettings.fields.strokeWidth')}
            >
              {[0, 1, 2, 3, 4, 5].map(width => (
                <MenuItem key={width} value={width}>{width === 0 ? t('ecommerceCategorySettings.none') : `${width} px`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Text Color Picker */}
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" display="block" gutterBottom>
            {t('ecommerceCategorySettings.fields.textColor')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Input
              type="color"
              value={settings.titleStyles?.color || '#ffffff'}
              onChange={(e) => handleTitleStyleChange('color', e.target.value)}
              sx={{
                padding: 0,
                border: 'none',
                width: '38px',
                height: '38px',
                minWidth: '38px',
                cursor: 'pointer',
                background: 'transparent',
                '&::-webkit-color-swatch-wrapper': { padding: 0 },
                '&::-webkit-color-swatch': { border: '1px solid #ccc', borderRadius: '4px' }
              }}
            />
            <Input
              value={settings.titleStyles?.color || '#ffffff'}
              onChange={(e) => handleTitleStyleChange('color', e.target.value)}
              size="small"
              sx={{ width: '80px' }}
            />
          </Box>
        </Grid>

        {/* Stroke Color Picker */}
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" display="block" gutterBottom>
            {t('ecommerceCategorySettings.fields.strokeColor')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Input
              type="color"
              value={settings.titleStyles?.strokeColor || '#000000'}
              onChange={(e) => handleTitleStyleChange('strokeColor', e.target.value)}
              disabled={!settings.titleStyles?.strokeWidth || settings.titleStyles.strokeWidth === 0}
              sx={{
                padding: 0,
                border: 'none',
                width: '38px',
                height: '38px',
                minWidth: '38px',
                cursor: 'pointer',
                background: 'transparent',
                '&::-webkit-color-swatch-wrapper': { padding: 0 },
                '&::-webkit-color-swatch': { border: '1px solid #ccc', borderRadius: '4px' }
              }}
            />
            <Input
              value={settings.titleStyles?.strokeColor || '#000000'}
              onChange={(e) => handleTitleStyleChange('strokeColor', e.target.value)}
              disabled={!settings.titleStyles?.strokeWidth || settings.titleStyles.strokeWidth === 0}
              size="small"
              sx={{ width: '80px' }}
            />
          </Box>
        </Grid>

        {/* Style Toggles */}
        <Grid item xs={6} sm={4} md={2}>
          <ToggleButtonGroup 
            size="small"
            value={(() => {
              const styles = [];
              if (settings.titleStyles?.bold) styles.push('bold');
              if (settings.titleStyles?.italic) styles.push('italic');
              return styles;
            })()}
            onChange={(_, newFormats) => {
              handleTitleStyleChange('bold', newFormats.includes('bold'));
              handleTitleStyleChange('italic', newFormats.includes('italic'));
            }}
          >
            <ToggleButton value="bold" aria-label="bold">
              <FormatBold />
            </ToggleButton>
            <ToggleButton value="italic" aria-label="italic">
              <FormatItalic />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        
        {/* Reset Button */}
        <Grid item xs={6} sm={8} md={2} sx={{ textAlign: 'right' }}>
          <Button
            onClick={resetTitleStyles}
            startIcon={<RestartAlt />}
            variant="outlined"
            color="secondary"
            size="small"
          >
            {t('ecommerceCategorySettings.actions.reset')}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <SectionTitle variant="h6">
        {t('ecommerceCategorySettings.previewTitle')}
      </SectionTitle>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceCategorySettings.previewDescription')}
      </Typography>

      {menuCategories.filter(cat => cat.isVisible ?? cat.visible).length > 0 ? (
        <Box sx={{ p: 2, border: '1px dashed grey', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <CategoryPreviewRenderer settings={settings} menuCategories={menuCategories} />
        </Box>
      ) : (
        <Alert severity="info">{t('ecommerceCategorySettings.previewEmpty')}</Alert>
      )}
    </Paper>
  );
};

export default CategorySettingsComponent; 